import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

/**
 * SoftBadge — translucent status badge for dark glass surfaces.
 * Uses state tokens with reduced opacity for a premium, low-contrast look
 * that still scans clearly. Prefer this over the hard-filled `Badge` on
 * glass surfaces; keep `Badge` for high-contrast cases (light-theme remnants).
 */

const softBadgeVariants = cva(
  // No border — was visually noisy on glass surfaces. text-emphasis4
  // already encodes weight 600 via the fontSize tuple, so font-medium
  // would conflict; intentionally omitted. `normal-case` is a defensive
  // baseline so chips don't inherit `uppercase` from container labels
  // (e.g. section headers like "RESIDENZA"). `w-fit` defends against
  // parent `flex flex-col` with default `align-items: stretch` that
  // would otherwise blow the badge to full width — overridable via
  // className (tw-merge wins later utilities).
  'inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-emphasis4 normal-case whitespace-nowrap',
  {
    variants: {
      tone: {
        // Neutral stays at /5 because the bg pairs with a dimmed
        // text-foreground/80 — already separates from the surface.
        neutral: 'bg-foreground/5 text-foreground/80',
        // Colored tones bumped to /25 so the chip identity reads on
        // dark surfaces (without the bg-tint at /15 alone was too
        // subtle once the colored border was dropped).
        success: 'bg-state-success/25 text-state-success',
        warning: 'bg-state-warning/25 text-state-warning',
        danger: 'bg-state-danger/25 text-state-danger',
        info: 'bg-state-info/25 text-state-info',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface SoftBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof softBadgeVariants> {}

export function SoftBadge({ className, tone, ...props }: SoftBadgeProps): JSX.Element {
  return <span className={cn(softBadgeVariants({ tone }), className)} {...props} />;
}

export { softBadgeVariants };
